import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, Alert, CircularProgress, Paper,
    Grid, MenuItem, Select, Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    LinkOff as UnlinkIcon  // Cambiado de Unlink a LinkOff
} from '@mui/icons-material';
import {
    getMikrotiks,
    getPools,
    getClientIPs,
    assignIPToClient,
    removeClientIP
} from '../api/mikrotik.api';
import ClienteBuscador from '../components/ClienteBuscador';

const MikroTikClientIP = () => {
    const [mikrotiks, setMikrotiks] = useState([]);
    const [selectedMikrotik, setSelectedMikrotik] = useState('');
    const [pools, setPools] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [clienteElegido, setClienteElegido] = useState(null);
    useEffect(() => { if (!openDialog) setClienteElegido(null); }, [openDialog]);
    const [formData, setFormData] = useState({
        cliente_id: '',
        mikrotik_id: '',
        pool_id: '',
        ip_asignada: '',
        nombre_usuario: '',
        velocidad_down: '',
        velocidad_up: '',
        mac_address: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadMikrotiks();
        loadClientes();
    }, []);

    useEffect(() => {
        if (selectedMikrotik) {
            loadPools();
            loadAsignaciones();
        }
    }, [selectedMikrotik]);

    const loadMikrotiks = async () => {
        try {
            const response = await getMikrotiks({ enabled_only: true });
            setMikrotiks(response.data);
            if (response.data.length > 0) {
                setSelectedMikrotik(response.data[0].id);
                setFormData(prev => ({ ...prev, mikrotik_id: response.data[0].id }));
            }
        } catch (error) {
            setError('Error cargando MikroTiks');
        }
    };

    const loadPools = async () => {
        if (!selectedMikrotik) return;
        try {
            const response = await getPools({ mikrotik_id: selectedMikrotik });
            setPools(response.data);
        } catch (error) {
            setError('Error cargando pools');
        }
    };

    const loadClientes = async () => {
        try {
            const response = [];
            setClientes(Array.isArray(response) ? response : response.data || []);
        } catch (error) {
            setError('Error cargando clientes');
        }
    };

    const loadAsignaciones = async () => {
        if (!selectedMikrotik) return;
        setLoading(true);
        try {
            const response = await getClientIPs({ mikrotik_id: selectedMikrotik });
            setAsignaciones(Array.isArray(response) ? response : response.data || []);
        } catch (error) {
            setError('Error cargando asignaciones');
        }
        setLoading(false);
    };

    const handleOpenDialog = () => {
        setFormData({
            cliente_id: '',
            mikrotik_id: selectedMikrotik,
            pool_id: '',
            ip_asignada: '',
            nombre_usuario: '',
            velocidad_down: '',
            velocidad_up: '',
            mac_address: ''
        });
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        if (!formData.cliente_id) {
            setError('Seleccione un cliente');
            return;
        }
        if (!formData.ip_asignada) {
            setError('Ingrese una dirección IP');
            return;
        }

        try {
            await assignIPToClient(formData);
            setSuccess('IP asignada exitosamente');
            setOpenDialog(false);
            loadAsignaciones();
        } catch (error) {
            setError(error.response?.data?.detail || 'Error al asignar IP');
        }
    };

    const handleLiberar = async (clienteId, ip) => {
        if (!window.confirm(`¿Liberar IP ${ip} de este cliente?`)) return;
        try {
            await removeClientIP(clienteId);
            setSuccess('IP liberada exitosamente');
            loadAsignaciones();
        } catch (error) {
            setError('Error al liberar IP');
        }
    };

    const getClienteNombre = (id) => {
        const cliente = clientes.find(c => c.id === id);
        if (cliente) {
            return `${cliente.nombres} ${cliente.apellidos}`;
        }
        return 'Desconocido';
    };

    const getClienteIdentificacion = (id) => {
        const cliente = clientes.find(c => c.id === id);
        return cliente ? cliente.identificacion : '-';
    };

    const getPoolNombre = (id) => {
        const pool = pools.find(p => p.id === id);
        return pool ? pool.nombre : '-';
    };

    const getMikrotikNombre = (id) => {
        const mik = mikrotiks.find(m => m.id === id);
        return mik ? mik.nombre : 'Desconocido';
    };

    return (
        <Box sx={{ p: 3 }}>
            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Asignación de IP a Clientes</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Select
                        value={selectedMikrotik}
                        onChange={(e) => setSelectedMikrotik(e.target.value)}
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        {mikrotiks.map(m => (
                            <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadAsignaciones}
                    >
                        Refrescar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenDialog}
                        disabled={!selectedMikrotik}
                    >
                        Asignar IP
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Cliente</TableCell>
                                <TableCell>Documento</TableCell>
                                <TableCell>IP Asignada</TableCell>
                                <TableCell>Pool</TableCell>
                                <TableCell>MikroTik</TableCell>
                                <TableCell>Usuario PPPoE</TableCell>
                                <TableCell>Velocidad</TableCell>
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {asignaciones.map((asig) => (
                                <TableRow key={asig.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {getClienteNombre(asig.cliente_id)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{getClienteIdentificacion(asig.cliente_id)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={asig.ip_asignada}
                                            color="primary"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{getPoolNombre(asig.pool_id)}</TableCell>
                                    <TableCell>{getMikrotikNombre(asig.mikrotik_id)}</TableCell>
                                    <TableCell>{asig.nombre_usuario || '-'}</TableCell>
                                    <TableCell>
                                        {asig.velocidad_down && asig.velocidad_up
                                            ? `${asig.velocidad_down}/${asig.velocidad_up} Mbps`
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleLiberar(asig.cliente_id, asig.ip_asignada)}
                                            title="Liberar IP"
                                        >
                                            <UnlinkIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {asignaciones.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography color="textSecondary">
                                            No hay IPs asignadas a clientes
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog para asignar IP */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Asignar IP a Cliente</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <ClienteBuscador
                              value={clienteElegido}
                              onChange={(c) => {
                                setClienteElegido(c);
                                setFormData({ ...formData, cliente_id: c?.id || '' });
                              }}
                              label="Cliente"
                              required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Select
                                fullWidth
                                value={formData.pool_id}
                                onChange={(e) => setFormData({ ...formData, pool_id: e.target.value })}
                                displayEmpty
                            >
                                <MenuItem value="">Sin pool</MenuItem>
                                {pools.map(p => (
                                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Dirección IP"
                                fullWidth
                                value={formData.ip_asignada}
                                onChange={(e) => setFormData({ ...formData, ip_asignada: e.target.value })}
                                placeholder="Ej: 192.168.1.100"
                                required
                                helperText="Esta IP se agregará a la address list 'CLIENTES' en MikroTik"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Usuario PPPoE (opcional)"
                                fullWidth
                                value={formData.nombre_usuario}
                                onChange={(e) => setFormData({ ...formData, nombre_usuario: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Velocidad Down (Mbps)"
                                type="number"
                                fullWidth
                                value={formData.velocidad_down}
                                onChange={(e) => setFormData({ ...formData, velocidad_down: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Velocidad Up (Mbps)"
                                type="number"
                                fullWidth
                                value={formData.velocidad_up}
                                onChange={(e) => setFormData({ ...formData, velocidad_up: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="MAC Address (opcional)"
                                fullWidth
                                value={formData.mac_address}
                                onChange={(e) => setFormData({ ...formData, mac_address: e.target.value })}
                                placeholder="Ej: AA:BB:CC:DD:EE:FF"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        Asignar IP
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MikroTikClientIP;
